import { FC, memo } from 'react';
import { isMobile as isMobileDevice } from 'react-device-detect';
import {
  Box,
  Button,
  HStack,
  Spacer,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import isEqual from 'lodash/fp/isEqual';
import { UserAvatar } from '../user-avatar';
import { ShareFundraiser } from '../share-fundraiser';
import { QuickLinks } from './QuickLinks';
import { getDataTracking, isMemorial } from '../../utils';
import styles from './UserNavigation.module.scss';
import { UserMenu } from '../layout/header/UserMenu';
import { Copy } from '../../assets';
import { NativeShare } from '../social-share';
import { useEventId } from '../../hooks';

export interface UserNavigationProps {
  avatar?: string;
  eventName?: string;
  firstName?: string;
  personalPageUrl?: string;
}

export const UserNavigation: FC<UserNavigationProps> = memo(
  ({
    avatar,
    eventName,
    firstName = '',
    personalPageUrl,
  }: UserNavigationProps) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const title = eventName || '';
    const url = personalPageUrl || '';
    const eventId = useEventId();

    return (
      <Box>
        <Stack
          className={styles.stickySidebar}
          px={[0, 8]}
          spacing={6}
          width={['full', 'full', '356px']}
        >
          {!isMemorial(String(eventId)) && (
            <Stack
              alignItems={['center']}
              direction={['row', 'column-reverse']}
              justifyContent={['center']}
            >
              <Box>
                <Text
                  fontSize="md"
                  fontWeight="bold"
                  color="brand.primary.swatch1"
                  align={['left', 'center']}
                  lineHeight="1"
                >
                  Welcome back,
                </Text>
                <Text
                  fontSize="1.5rem"
                  fontWeight="bold"
                  color="brand.primary.swatch1"
                  align={['left', 'center']}
                  textTransform="capitalize"
                >
                  {firstName}!
                </Text>
              </Box>
              <Spacer />
              <Box>
                <UserAvatar
                  size="lg"
                  src={avatar}
                  name={firstName}
                  aria-label={`${firstName} Avatar`}
                />
              </Box>
            </Stack>
          )}

          <UserMenu pb={8} spacing={8} display={['none', 'none', 'flex']} />

          <Stack spacing={7}>
            <Button
              as="a"
              size="lg"
              type="button"
              {...getDataTracking('customizeMyPageLink')}
              href={personalPageUrl || '#'}
              style={{
                color: 'white',
                textDecoration: 'none',
              }}
            >
              Customize My Page
            </Button>
            {isMobileDevice ? (
              <NativeShare title={title} url={url}>
                Share Fundraiser Link
              </NativeShare>
            ) : (
              <Button
                size="lg"
                colorScheme="blue"
                leftIcon={<Copy boxSize={6} />}
                onClick={onOpen}
                {...getDataTracking('sidebar share fundraiser button')}
              >
                Share Fundraiser Link
              </Button>
            )}
            {isOpen && (
              <ShareFundraiser
                isOpen={isOpen}
                onClose={onClose}
                personalPageUrl={personalPageUrl}
                trackingLabel="sidebar"
              />
            )}
          </Stack>

          <HStack justify="space-around">
            <QuickLinks personalPageUrl={personalPageUrl} />
          </HStack>
        </Stack>
      </Box>
    );
  },
  isEqual
);
